import { laneLifecyclePhase } from '@hopdrive/feature-lane-lifecycle';

/** The lane fields that drive enrichment state and estimate display. */
export interface LaneEstimateFields {
  id?: number | null;
  status?: string | null;
  distance_miles?: number | null;
  tolls?: number | null;
  duration_sec?: number | null;
  enriched_at?: string | null;
  route_polyline?: string | null;
  /** The database's time of the lane's last status write; the rerun guards compare against it. */
  updatedat?: string | null;
}

/** A lane is enriched only once its lifecycle status is canonically ready. Estimates
 *  alone do not imply readiness: a stale lane can still carry complete route data. */
export function isLaneEnriched(lane?: LaneEstimateFields | null): boolean {
  return laneLifecyclePhase(lane) === 'ready';
}

/** A persisted lane still awaiting routing estimates should be polled for enrichment. */
export function laneNeedsEnrichment(lane?: LaneEstimateFields | null): boolean {
  return laneLifecyclePhase(lane) === 'enriching';
}

/** Merge freshly-enriched routing estimates onto an existing lane, preserving its
 *  attached locations, favorite flag, and any other fields. */
export function mergeLaneEstimates<T extends LaneEstimateFields>(
  existing: T,
  enriched: LaneEstimateFields
): T {
  return {
    ...existing,
    status: enriched.status,
    distance_miles: enriched.distance_miles,
    tolls: enriched.tolls,
    duration_sec: enriched.duration_sec,
    enriched_at: enriched.enriched_at,
    route_polyline: enriched.route_polyline,
    updatedat: enriched.updatedat ?? existing.updatedat,
  };
}

/** The lane as a rerun left it: the status and `updatedat` its last landed write returned, or null when none landed. */
export function laneAfterRerun<T extends LaneEstimateFields>(
  lane: T,
  rerun: { status: string | null; updatedat: string | null }
): T | null {
  if (!rerun.status) return null;
  return { ...lane, status: rerun.status, updatedat: rerun.updatedat };
}
